/**
 * Telehealth Selectors
 *
 * Memoized selectors for accessing telehealth state.
 * Follows the pattern from other features with fallback for undefined state.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { FeatureSelectorWithFallback } from '@store/storeTypes';
import type { RootState } from '@store/types';

import type { TelehealthState } from '../types';

/**
 * Empty state fallback (when feature not loaded)
 */
const emptyState: TelehealthState = {
  activeSession: null,
  deviceSettings: {
    selectedCameraId: null,
    selectedSpeakerId: null,
    noiseCancellationEnabled: false,
    backgroundBlurMode: 'none',
    hideSelfView: false,
  },
  preCallSettings: {
    isInPreCall: false,
    isCameraOn: true,
    isMicOn: false,
    userName: '',
  },
  preferences: {
    skipPreCallLobby: true,
    waitingRoomLauncherYPercent: 50,
  },
  callPanelRect: null,
  chatMessages: [],
  chatUnreadCount: 0,
  isChatPanelOpen: false,
  encounterMode: 'standard',
  waitingRoomLaunchIntent: null,
};

/**
 * Base selector - returns telehealth state or empty fallback
 */
export const selectTelehealthState: FeatureSelectorWithFallback<
  TelehealthState,
  typeof emptyState
> = (state: RootState) => state.telehealth || emptyState;

// ===== Session Selectors =====

export const selectActiveSession = createSelector(
  [selectTelehealthState],
  state => state.activeSession,
);

export const selectHasActiveSession = createSelector(
  [selectActiveSession],
  session => session !== null,
);

export const selectIsSessionOwner = createSelector(
  [selectActiveSession],
  session => session?.isOwner ?? false,
);

// ===== Device Settings Selectors =====

export const selectDeviceSettings = createSelector(
  [selectTelehealthState],
  state => state.deviceSettings,
);

export const selectSelectedCameraId = createSelector(
  [selectDeviceSettings],
  settings => settings.selectedCameraId,
);

export const selectSelectedSpeakerId = createSelector(
  [selectDeviceSettings],
  settings => settings.selectedSpeakerId,
);

export const selectNoiseCancellationEnabled = createSelector(
  [selectDeviceSettings],
  settings => settings.noiseCancellationEnabled,
);

export const selectBackgroundBlurMode = createSelector(
  [selectDeviceSettings],
  settings => settings.backgroundBlurMode,
);

export const selectHideSelfView = createSelector(
  [selectDeviceSettings],
  settings => settings.hideSelfView,
);

// ===== Pre-Call Settings Selectors =====

export const selectPreCallSettings = createSelector(
  [selectTelehealthState],
  state => state.preCallSettings,
);

export const selectIsInPreCall = createSelector(
  [selectPreCallSettings],
  settings => settings.isInPreCall,
);

export const selectIsCameraOn = createSelector(
  [selectPreCallSettings],
  settings => settings.isCameraOn,
);

export const selectIsMicOn = createSelector([selectPreCallSettings], settings => settings.isMicOn);

export const selectUserName = createSelector(
  [selectPreCallSettings],
  settings => settings.userName,
);

export const selectTelehealthPreferences = createSelector(
  [selectTelehealthState],
  state => state.preferences ?? emptyState.preferences,
);

export const selectSkipPreCallLobbyPreference = createSelector(
  [selectTelehealthPreferences],
  preferences => preferences.skipPreCallLobby,
);

export const selectWaitingRoomLauncherYPercent = createSelector(
  [selectTelehealthPreferences],
  preferences =>
    preferences.waitingRoomLauncherYPercent ?? emptyState.preferences.waitingRoomLauncherYPercent,
);

// ===== Mini-Player Selectors =====

export const selectCallPanelRect = createSelector(
  [selectTelehealthState],
  state => state.callPanelRect,
);

// ===== Chat Selectors =====

export const selectChatMessages = createSelector(
  [selectTelehealthState],
  state => state.chatMessages,
);

export const selectChatUnreadCount = createSelector(
  [selectTelehealthState],
  state => state.chatUnreadCount,
);

export const selectIsChatPanelOpen = createSelector(
  [selectTelehealthState],
  state => state.isChatPanelOpen,
);

// ===== Encounter Mode Selectors =====

export const selectEncounterMode = createSelector(
  [selectTelehealthState],
  state => state.encounterMode,
);

export const selectWaitingRoomLaunchIntent = createSelector(
  [selectTelehealthState],
  state => state.waitingRoomLaunchIntent,
);
